import { Yakureki } from '@/shared/types/yakurekiCore'
import { getYakurekiDelta } from './yakureki'
import { fetchAllYakurekiPages } from './yakurekiPagination'

/**
 * 薬歴データのマージ処理
 */
export const mergeYakurekiData = (
  existing: Yakureki[],
  updates: Yakureki[],
  displayDays = 3
): Yakureki[] => {
  const existingMap = new Map(existing.map((item) => [item.id, item]))

  updates.forEach((update) => {
    existingMap.set(update.id, update)
  })

  const cutoffDate = new Date()
  cutoffDate.setDate(cutoffDate.getDate() - displayDays)

  return Array.from(existingMap.values())
    .filter((item) => new Date(item.created_at) >= cutoffDate)
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
}

/**
 * 一覧の永続キャッシュ (yakureki_data) から event を名指しで外す。
 * delta 同期は updated_at 起点で hard delete を表現できない (count 0 ならキャッシュを
 * そのまま返す) ため、破棄で back が削除したフリー録音 (cancel 応答の deleted_event_ids) は
 * 呼び出し側がここで外してから一覧を invalidate する。
 */
export const removeYakurekiFromLocalCache = (ids: readonly string[]): void => {
  if (ids.length === 0) return
  let existing: Yakureki[] = []
  try {
    existing = JSON.parse(localStorage.getItem('yakureki_data') || '[]')
  } catch {
    existing = []
  }
  const drop = new Set(ids)
  localStorage.setItem(
    'yakureki_data',
    JSON.stringify(existing.filter((item) => !drop.has(item.id)))
  )
}

/**
 * 差分同期を使用した薬歴データの取得
 * LocalStorageを使用してキャッシュとの差分を管理
 */
export const fetchYakurekiWithDelta = async (
  displayDays = 3
): Promise<Yakureki[]> => {
  const lastUpdated = localStorage.getItem('yakureki_last_updated')

  if (!lastUpdated) {
    const results = await fetchAllYakurekiPages()
    const now = new Date().toISOString()
    localStorage.setItem('yakureki_last_updated', now)
    localStorage.setItem('yakureki_data', JSON.stringify(results))
    return results
  }

  try {
    // 本番環境でのサーバー更新遅延を考慮して、30秒前のタイムスタンプで差分取得
    const lastUpdatedDate = new Date(lastUpdated)
    const adjustedTime = new Date(lastUpdatedDate.getTime() - 30000) // 30秒前
    const adjustedLastUpdated = adjustedTime.toISOString()

    const response = await getYakurekiDelta(adjustedLastUpdated)
    const deltaData = response.data
    // 遠隔の店から見えなくなった処方 (依頼の取り消し・お断り。back log/250) を外す
    removeYakurekiFromLocalCache(deltaData.removed_ids ?? [])

    if (deltaData.count > 0) {
      let existing: Yakureki[] = []
      try {
        existing = JSON.parse(localStorage.getItem('yakureki_data') || '[]')
      } catch {
        existing = []
      }
      const merged = mergeYakurekiData(existing, deltaData.results, displayDays)
      localStorage.setItem('yakureki_data', JSON.stringify(merged))
      localStorage.setItem('yakureki_last_updated', deltaData.last_updated)
      return merged
    }

    let existing: Yakureki[] = []
    try {
      existing = JSON.parse(localStorage.getItem('yakureki_data') || '[]')
    } catch {
      existing = []
    }
    return existing
  } catch {
    const results = await fetchAllYakurekiPages()
    const now = new Date().toISOString()
    localStorage.setItem('yakureki_last_updated', now)
    localStorage.setItem('yakureki_data', JSON.stringify(results))
    return results
  }
}
